import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Slider, Box, Typography, DialogActions, Button, ToggleButtonGroup, ToggleButton, Stack, Chip } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LayersIcon from '@mui/icons-material/Layers';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CompareIcon from '@mui/icons-material/Compare';
import { API_BASE_URL } from '../../config';

interface Props {
  open: boolean;
  assetA: any; // minimal { _id, filePath, previewPaths }
  assetB: any;
  onClose: () => void;
}

type CompareMode = 'split' | 'overlay' | 'sideBySide' | 'onionSkin';

const DuplicateCompareDialog: React.FC<Props> = ({ open, assetA, assetB, onClose }) => {
  const [split, setSplit] = useState(50);
  const [opacity, setOpacity] = useState(50);
  const [mode, setMode] = useState<CompareMode>('overlay');

  const base = API_BASE_URL || (process.env.REACT_APP_API_BASE_URL as string) || '';
  const srcA = useMemo(() => assetA?.previewPaths?.medium ? `${base}${assetA.previewPaths.medium}` : `${base}/${(assetA?.filePath || '').replace(/\\/g, '/')}` , [assetA]);
  const srcB = useMemo(() => assetB?.previewPaths?.medium ? `${base}${assetB.previewPaths.medium}` : `${base}/${(assetB?.filePath || '').replace(/\\/g, '/')}` , [assetB]);

  const renderComparison = () => {
    switch (mode) {
      case 'overlay':
        // Överlagringsläge med justerbar transparens
        return (
          <Box sx={{ position: 'relative', width: '100%', height: 600, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box component="img" src={srcB} alt="B" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
            <Box 
              component="img" 
              src={srcA} 
              alt="A" 
              sx={{ 
                position: 'absolute', 
                inset: 0, 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                opacity: opacity / 100
              }} 
            />
            <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1 }}>
              <Chip label="A" size="small" sx={{ bgcolor: 'rgba(25, 118, 210, 0.9)', color: 'white' }} />
              <Chip label="B" size="small" sx={{ bgcolor: 'rgba(211, 47, 47, 0.9)', color: 'white' }} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, bgcolor: 'rgba(0,0,0,0.7)', p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="#fff" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                Justera transparens för att se skillnader
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="caption" color="#fff" sx={{ minWidth: 40 }}>B (100%)</Typography>
                <Slider 
                  value={opacity} 
                  onChange={(_, v) => setOpacity(v as number)} 
                  min={0} 
                  max={100}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `A: ${v}%`}
                  sx={{ color: '#1976d2', flex: 1 }} 
                />
                <Typography variant="caption" color="#fff" sx={{ minWidth: 40 }}>A (0%)</Typography>
              </Stack>
            </Box>
          </Box>
        );

      case 'split':
        // Split-view med vertikal delning
        return (
          <Box sx={{ position: 'relative', width: '100%', height: 600, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box component="img" src={srcB} alt="B" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', width: `${split}%` }}>
              <Box 
                component="img" 
                src={srcA} 
                alt="A" 
                sx={{ 
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain'
                }} 
              />
            </Box>
            {/* Delningslinje */}
            <Box 
              sx={{ 
                position: 'absolute', 
                left: `${split}%`, 
                top: 0, 
                bottom: 0, 
                width: 4, 
                bgcolor: '#1976d2',
                transform: 'translateX(-2px)',
                cursor: 'ew-resize',
                boxShadow: '0 0 8px rgba(25, 118, 210, 0.5)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 32,
                  height: 32,
                  bgcolor: '#1976d2',
                  borderRadius: '50%',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }
              }} 
            />
            <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
              <Chip label="A" size="small" sx={{ bgcolor: 'rgba(25, 118, 210, 0.9)', color: 'white' }} />
            </Box>
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <Chip label="B" size="small" sx={{ bgcolor: 'rgba(211, 47, 47, 0.9)', color: 'white' }} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, bgcolor: 'rgba(0,0,0,0.7)', p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="#fff" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                Dra reglaget för att jämföra
              </Typography>
              <Slider 
                value={split} 
                onChange={(_, v) => setSplit(v as number)} 
                min={0} 
                max={100}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{ color: '#1976d2' }} 
              />
            </Box>
          </Box>
        );

      case 'sideBySide':
        // Sida vid sida
        return (
          <Box sx={{ display: 'flex', gap: 2, height: 600, bgcolor: '#000', p: 2 }}>
            <Box sx={{ flex: 1, position: 'relative', border: '2px solid #1976d2', borderRadius: 2, overflow: 'hidden' }}>
              <Box component="img" src={srcA} alt="A" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              <Chip 
                label="A" 
                size="small" 
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  left: 8, 
                  bgcolor: 'rgba(25, 118, 210, 0.9)', 
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
              <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 8, bgcolor: 'rgba(0,0,0,0.7)', p: 1, borderRadius: 1 }}>
                <Typography variant="caption" color="#fff">{assetA?.title || assetA?.fileName || 'Bild A'}</Typography>
              </Box>
            </Box>
            <Box sx={{ flex: 1, position: 'relative', border: '2px solid #d32f2f', borderRadius: 2, overflow: 'hidden' }}>
              <Box component="img" src={srcB} alt="B" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              <Chip 
                label="B" 
                size="small" 
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  left: 8, 
                  bgcolor: 'rgba(211, 47, 47, 0.9)', 
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
              <Box sx={{ position: 'absolute', bottom: 8, left: 8, right: 8, bgcolor: 'rgba(0,0,0,0.7)', p: 1, borderRadius: 1 }}>
                <Typography variant="caption" color="#fff">{assetB?.title || assetB?.fileName || 'Bild B'}</Typography>
              </Box>
            </Box>
          </Box>
        );

      case 'onionSkin':
        // Onion skin - blixtvis växling mellan bilderna
        return (
          <Box sx={{ position: 'relative', width: '100%', height: 600, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box component="img" src={srcB} alt="B" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
            <Box 
              component="img" 
              src={srcA} 
              alt="A" 
              sx={{ 
                position: 'absolute', 
                inset: 0, 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                opacity: opacity / 100,
                mixBlendMode: 'difference'
              }} 
            />
            <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1 }}>
              <Chip label="Difference Mode" size="small" sx={{ bgcolor: 'rgba(156, 39, 176, 0.9)', color: 'white' }} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, bgcolor: 'rgba(0,0,0,0.7)', p: 2, borderRadius: 2 }}>
              <Typography variant="caption" color="#fff" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                Skillnader visas tydligare (vitt = skillnad, svart = lika)
              </Typography>
              <Slider 
                value={opacity} 
                onChange={(_, v) => setOpacity(v as number)} 
                min={0} 
                max={100}
                valueLabelDisplay="auto"
                sx={{ color: '#9c27b0' }} 
              />
            </Box>
          </Box>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
          <Typography variant="h6">Jämför bilder (A/B)</Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, newMode) => newMode && setMode(newMode)}
            size="small"
          >
            <ToggleButton value="overlay" aria-label="överlagring">
              <LayersIcon sx={{ mr: 0.5 }} fontSize="small" />
              Överlagring
            </ToggleButton>
            <ToggleButton value="split" aria-label="delad vy">
              <CompareIcon sx={{ mr: 0.5 }} fontSize="small" />
              Delad vy
            </ToggleButton>
            <ToggleButton value="sideBySide" aria-label="sida vid sida">
              <ViewColumnIcon sx={{ mr: 0.5 }} fontSize="small" />
              Sida vid sida
            </ToggleButton>
            <ToggleButton value="onionSkin" aria-label="skillnadsläge">
              <SwapHorizIcon sx={{ mr: 0.5 }} fontSize="small" />
              Skillnader
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {renderComparison()}
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 1 }}>
          {mode === 'overlay' && 'Justera transparensen för att se båda bilderna samtidigt'}
          {mode === 'split' && 'Dra reglaget för att se mer av A eller B'}
          {mode === 'sideBySide' && 'Jämför bilderna sida vid sida'}
          {mode === 'onionSkin' && 'Skillnader mellan bilderna visas i vitt'}
        </Typography>
        <Button onClick={onClose} variant="contained">Stäng</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateCompareDialog;


